import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adminLogin } from '../menuApi';

// No credentials in this file, and none in the bundle.
//
// This page used to compare the entered username and password against
// `REACT_APP_ADMIN_USER` / `REACT_APP_ADMIN_PASS`, with `'redspeed123'` hardcoded
// as the fallback. Create React App inlines every REACT_APP_* variable into the
// JavaScript it ships, so all three were readable by any visitor to
// gapgo.redspeed.com.au via view-source — verified against the live bundle.
//
// The check now happens in the `menu` Lambda against a SHA-256 hash that exists
// only in its environment, and returns a short-lived signed token carrying an
// admin role claim. See backend/functions/menu/index.js `adminLogin`.

export default function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleLogin = async () => {
    if (!username.trim() || !password) { setError('Please fill in all fields'); return; }
    setLoading(true);
    setError('');
    try {
      // The server delays every attempt equally, right or wrong, so this is not a
      // fast oracle to brute-force against. No artificial delay needed here.
      await adminLogin(username.trim(), password);
      navigate('/admin');
    } catch (err) {
      setError((err && err.message) || 'Sign-in unavailable — try again');
      setLoading(false);
    }
  };

  return (
    <div style={S.page}>
      <div style={S.grain} />
      <motion.div style={{ ...S.blob, top: '-120px', right: '-100px', background: 'radial-gradient(circle, rgba(56,189,248,0.12) 0%, transparent 70%)' }}
        animate={{ x: [0, 40, -20, 0], y: [0, -30, 20, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div style={{ ...S.blob, bottom: '-80px', left: '-120px', background: 'radial-gradient(circle, rgba(167,139,250,0.10) 0%, transparent 70%)' }}
        animate={{ x: [0, -30, 40, 0], y: [0, 40, -20, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut', delay: 8 }}
      />

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 160, damping: 20 }}
        style={S.card}
      >
        {/* Brand */}
        <div style={S.brand}>
          <motion.span
            animate={{ scale: [1, 1.6, 1], opacity: [1, 0.4, 1] }}
            transition={{ duration: 2.6, repeat: Infinity }}
            style={S.brandDot}
          />
          <span style={S.brandName}>GapGo</span>
        </div>

        <div style={S.shimmer} />

        <h1 style={S.heading}>Super Admin</h1>
        <p style={S.sub}>Sign in to access the dashboard</p>

        {/* Username */}
        <div style={S.field}>
          <label style={S.label}>USERNAME</label>
          <input
            style={S.input}
            placeholder="admin"
            value={username}
            autoCapitalize="none"
            autoCorrect="off"
            onChange={(e) => { setUsername(e.target.value); setError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
          />
        </div>

        {/* Password */}
        <div style={S.field}>
          <label style={S.label}>PASSWORD</label>
          <div style={S.passWrap}>
            <input
              style={{ ...S.input, flex: 1, marginBottom: 0 }}
              placeholder="••••••••"
              type={showPass ? 'text' : 'password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            />
            <button style={S.eyeBtn} onClick={() => setShowPass(p => !p)} tabIndex={-1}>
              {showPass ? '🙈' : '👁'}
            </button>
          </div>
        </div>

        {error && (
          <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} style={S.error}>
            <span>⚠</span> {error}
          </motion.div>
        )}

        <motion.button
          whileTap={{ scale: 0.97 }}
          style={{ ...S.signInBtn, opacity: loading ? 0.7 : 1 }}
          onClick={handleLogin}
          disabled={loading}
        >
          {loading ? 'Signing in…' : 'Sign in →'}
        </motion.button>

        <p style={S.note}>Authorised personnel only</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        style={S.footer}
      >
        <span style={S.footerLabel}>POWERED BY</span>
        <a href="https://www.redspeed.com.au" target="_blank" rel="noopener noreferrer" style={S.footerLink}>
          Redspeed Technologies
        </a>
      </motion.div>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100vh',
    background: '#050507',
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 16px',
  },
  grain: {
    position: 'fixed', inset: 0, opacity: 0.025, pointerEvents: 'none', zIndex: 0,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'repeat', backgroundSize: '180px',
  },
  blob: { position: 'absolute', width: '480px', height: '480px', borderRadius: '50%', pointerEvents: 'none', zIndex: 0 },
  card: {
    width: '100%', maxWidth: '400px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '28px',
    padding: '36px 28px 32px',
    position: 'relative', zIndex: 1,
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    boxShadow: '0 40px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.07)',
  },
  brand: { display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '28px' },
  brandDot: {
    width: '9px', height: '9px', borderRadius: '50%',
    background: '#fff', boxShadow: '0 0 14px rgba(255,255,255,0.9)',
    display: 'inline-block', flexShrink: 0,
  },
  brandName: { fontSize: '18px', fontWeight: '800', color: '#fff', letterSpacing: '-0.5px' },
  shimmer: {
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)',
    marginBottom: '28px',
  },
  heading: { fontSize: '30px', fontWeight: '300', color: '#fff', letterSpacing: '-1.2px', margin: '0 0 6px' },
  sub: { fontSize: '13px', color: '#4B5563', margin: '0 0 28px' },
  field: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' },
  label: { fontSize: '10px', letterSpacing: '2.5px', color: '#374151', fontWeight: '600' },
  input: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.09)',
    borderRadius: '14px',
    padding: '14px 16px',
    color: '#fff',
    fontSize: '15px',
    fontFamily: 'inherit',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    marginBottom: 0,
    transition: 'border-color 0.2s',
  },
  passWrap: { display: 'flex', gap: '8px', alignItems: 'center' },
  eyeBtn: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.09)',
    borderRadius: '12px',
    padding: '0 14px',
    fontSize: '16px',
    cursor: 'pointer',
    height: '48px',
    flexShrink: 0,
  },
  error: {
    display: 'flex', alignItems: 'center', gap: '8px',
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.25)',
    borderRadius: '12px',
    padding: '11px 14px',
    fontSize: '13px',
    color: '#FCA5A5',
    marginBottom: '16px',
  },
  signInBtn: {
    width: '100%',
    background: '#fff',
    color: '#050507',
    border: 'none',
    borderRadius: '15px',
    padding: '16px',
    fontSize: '16px',
    fontWeight: '700',
    fontFamily: 'inherit',
    cursor: 'pointer',
    letterSpacing: '0.2px',
    marginTop: '8px',
    marginBottom: '16px',
  },
  note: { textAlign: 'center', fontSize: '12px', color: '#1F2937', margin: 0 },
  footer: {
    display: 'flex', alignItems: 'center', gap: '8px',
    marginTop: '32px', position: 'relative', zIndex: 1,
  },
  footerLabel: { fontSize: '9px', letterSpacing: '3px', color: '#1F2937', fontWeight: '600' },
  footerLink: { fontSize: '11px', color: '#4B5563', textDecoration: 'none', fontWeight: '600' },
};
